'use client';

import type { ReactNode } from 'react';

import { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';

import { RADIUS, SHADOW } from 'src/theme/ui-tokens';

import { Iconify } from 'src/components/iconify';
import { MediaShot } from 'src/components/cmui/media-shot';
import { LoadingImg } from 'src/components/cmui/loading-img';

import { ScrubVideo } from './scrub-video';

import type { MediaSlide } from '../utils/search-all-media-slides';

/**
 * 상세 다이얼로그 왼쪽 — 한 칸씩 크게 보고, 여러 장이면 좌우 화살표 · 「n/N」 · 아래 썸네일 줄로 넘긴다.
 * 영상 칸은 재생 바가 늘 보이고(받아 둔 뒤 · `alwaysControls`) 마우스 훑기가 된다(`ScrubVideo`). 칸이 없으면 `empty` 를 그린다.
 */
export function MediaCarousel({ slides, light = false, empty, resetKey }: { slides: MediaSlide[]; light?: boolean; empty: ReactNode; resetKey: string }) {
  const [index, setIndex] = useState(0);
  const [broken, setBroken] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    setIndex(0);
    setBroken(new Set());
  }, [resetKey]);

  const visible = slides.filter((s) => s.type === 'video' || !broken.has(s.image));
  if (visible.length === 0) return <>{empty}</>;

  const current = visible[Math.min(index, visible.length - 1)];
  const many = visible.length > 1;
  const go = (step: number) => setIndex((i) => (i + step + visible.length) % visible.length);
  const arrowSx = { position: 'absolute', top: '50%', transform: 'translateY(-50%)', zIndex: 2, bgcolor: 'rgba(255,255,255,0.9)', boxShadow: SHADOW.raised, '&:hover': { bgcolor: 'background.paper' } } as const;

  return (
    <Stack sx={{ width: 1, height: 1, bgcolor: light ? 'common.white' : 'grey.900' }}>
      <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
        {current.type === 'video' ? (
          <ScrubVideo key={current.video} src={current.video} poster={current.image} alwaysControls />
        ) : (
          <LoadingImg
            key={current.image}
            src={current.image}
            alt=""
            onError={() => setBroken((prev) => new Set(prev).add(current.image))}
            sx={{ width: 1, height: 1, objectFit: 'contain', display: 'block' }}
          />
        )}
        {many && (
          <>
            <IconButton aria-label="이전 이미지" onClick={() => go(-1)} size="small" sx={{ ...arrowSx, left: 12 }}>
              <Iconify icon="solar:alt-arrow-left-linear" width={18} />
            </IconButton>
            <IconButton aria-label="다음 이미지" onClick={() => go(1)} size="small" sx={{ ...arrowSx, right: 12 }}>
              <Iconify icon="solar:alt-arrow-right-linear" width={18} />
            </IconButton>
            <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 2, px: 1, py: 0.25, borderRadius: RADIUS.pill, bgcolor: 'rgba(0,0,0,0.6)', color: 'common.white', typography: 'caption', fontVariantNumeric: 'tabular-nums' }}>
              {Math.min(index, visible.length - 1) + 1}/{visible.length}
            </Box>
          </>
        )}
      </Box>
      {many && (
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0, px: 1.5, py: 1, overflowX: 'auto', bgcolor: light ? 'grey.100' : 'rgba(0,0,0,0.6)' }}>
          {visible.map((s, i) => (
            <Box
              key={`${s.image}-${i}`}
              role="button"
              aria-label={`${i + 1}번째 보기`}
              onClick={() => setIndex(i)}
              sx={{
                position: 'relative',
                flexShrink: 0,
                width: 52,
                height: 52,
                borderRadius: RADIUS.sm,
                cursor: 'pointer',
                border: 2,
                borderColor: i === Math.min(index, visible.length - 1) ? 'primary.main' : 'transparent',
                overflow: 'hidden',
              }}
            >
              <MediaShot src={s.image ?? ''} width={1} height={1} radius={0}>
                {s.type === 'video' && <Iconify icon="solar:play-circle-linear" width={18} sx={{ position: 'absolute', inset: 0, m: 'auto', color: 'common.white' }} />}
              </MediaShot>
            </Box>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
